import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useCheckMsisdnAvailability } from "@/services/api";
import { PHONE_REGEX } from "@/utils/constants";
import { extractAndParseJSON } from "@/utils/stringUtils";

import { formatMsisdnForApi } from "./NumberPorting.utils";

/**
 * Constants for MSISDN validation status.
 */
export const MSISDN_VALIDATION_STATUS = {
  IDLE: "idle",
  LOADING: "loading",
  VALID: "valid",
  INVALID: "invalid",
} as const;

/**
 * Represents the current status of MSISDN validation.
 */
export type MsisdnValidationStatus =
  (typeof MSISDN_VALIDATION_STATUS)[keyof typeof MSISDN_VALIDATION_STATUS];

/**
 * Hook for validating the availability of a phone number (MSISDN).
 * Handles the communication with the MSISDN availability API.
 *
 * @returns An object containing validation status, error messages, and the validation function.
 */
export const useMsisdnValidation = () => {
  const { t } = useTranslation();
  const [msisdnValidationStatus, setMsisdnValidationStatus] =
    useState<MsisdnValidationStatus>(MSISDN_VALIDATION_STATUS.IDLE);
  const [msisdnError, setMsisdnError] = useState<string>("");

  /**
   * Mutation hook to check MSISDN availability via API.
   */
  const { mutate: checkMsisdnAvailability } = useCheckMsisdnAvailability({
    mutation: {
      onSuccess: (data) => {
        // Map API response status to local state and translations.
        if (data.msisdnStatus === "ALREADY_IN_USE") {
          setMsisdnValidationStatus(MSISDN_VALIDATION_STATUS.INVALID);
          setMsisdnError(
            t("Onboarding.sections.number-porting.msisdnAlreadyInUse"),
          );
        } else if (data.msisdnStatus === "SUSPENDED") {
          setMsisdnValidationStatus(MSISDN_VALIDATION_STATUS.INVALID);
          setMsisdnError(
            t("Onboarding.sections.number-porting.msisdnSuspended"),
          );
        } else if (data.msisdnStatus === "AVAILABLE") {
          setMsisdnValidationStatus(MSISDN_VALIDATION_STATUS.VALID);
          setMsisdnError("");
        } else {
          setMsisdnValidationStatus(MSISDN_VALIDATION_STATUS.INVALID);
          setMsisdnError(t("Common.generalForm.error.genericError"));
        }
      },
      onError: (error: any) => {
        setMsisdnValidationStatus(MSISDN_VALIDATION_STATUS.INVALID);
        const errorStatus = error?.response?.status;
        const errorData = error?.response?.data;

        // Special handling for 400 errors, which often contain specific reasons.
        if (errorStatus === 400) {
          const parsedData = extractAndParseJSON(errorData);
          if (parsedData?.msisdnStatus === "ALREADY_IN_USE") {
            setMsisdnError(
              t("Onboarding.sections.number-porting.msisdnAlreadyInUse"),
            );

            return;
          }
          // Alternative error message from backend for active SIMs.
          if (
            typeof errorData === "string" &&
            errorData.includes("SIM ist bereits aktiv ausgespielt")
          ) {
            setMsisdnError(
              t("Onboarding.sections.number-porting.msisdnAlreadyInUse"),
            );

            return;
          }
        }
        setMsisdnError(t("Common.generalForm.error.genericError"));
      },
    },
  });

  /**
   * Validates a given MSISDN string.
   * Only triggers API check if format is correct and not already loading.
   *
   * @param currentValue - The current phone number string to validate.
   */
  const validateMsisdn = (currentValue: string) => {
    if (
      !currentValue ||
      msisdnValidationStatus === MSISDN_VALIDATION_STATUS.LOADING
    ) {
      return;
    }

    if (!PHONE_REGEX.test(currentValue)) {
      return;
    }

    const formattedMsisdn = formatMsisdnForApi(currentValue);
    if (formattedMsisdn.length !== 10) {
      return;
    }

    setMsisdnValidationStatus(MSISDN_VALIDATION_STATUS.LOADING);
    checkMsisdnAvailability({ msisdn: formattedMsisdn });
  };

  return {
    msisdnValidationStatus,
    setMsisdnValidationStatus,
    msisdnError,
    setMsisdnError,
    validateMsisdn,
  };
};
